import axios, { AxiosResponse, AxiosError } from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

// Configure axios defaults
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 seconds timeout
});

// Set up axios defaults for all requests
axios.defaults.baseURL = API_BASE_URL;

// Add request interceptor for logging and auth
api.interceptors.request.use(
  (config) => {
    console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);

    // Add auth token if available
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response: AxiosResponse) => {
    console.log(`API Response: ${response.status} ${response.config.url}`);
    return response;
  },
  (error: AxiosError) => {
    console.error('API Response Error:', error.response?.status, error.message, error.response?.data);

    if (error.response) {
      // Server responded with error status
      const errorMessage = error.response.data || `HTTP ${error.response.status}`;
      throw new ApiError(error.response.status, JSON.stringify(errorMessage), error.response.data);
    } else if (error.request) {
      // Request was made but no response received
      throw new ApiError(0, 'Network error or server unavailable');
    } else {
      // Something else happened
      throw new ApiError(0, error.message || 'Unknown error occurred');
    }
  }
);

class ApiError extends Error {
  constructor(public status: number, message: string, public data?: any) {
    super(message);
    this.name = 'ApiError';
  }
}

async function apiRequest<T>(endpoint: string, options: any = {}): Promise<T> {
  try {
    const response = await api.request({
      url: endpoint,
      ...options,
    });
    return response.data;
  } catch (error) {
    throw error; // Re-throw as interceptor already handled it
  }
}

export const authApi = {
  login: (email: string, password: string) =>
    apiRequest<{ success: boolean; token: string; user: any }>('/api/auth/login', {
      method: 'POST',
      data: { email, password },
    }),

  register: (email: string, password: string) =>
    apiRequest<{ success: boolean; token: string; user: any }>('/api/auth/register', {
      method: 'POST',
      data: { email, password },
    }),

  me: () => apiRequest<{ user: any }>('/api/auth/me'),

  logout: () =>
    apiRequest<{ success: boolean }>('/api/auth/logout', {
      method: 'POST',
    }),
};

// Account API
export const accountApi = {
  // Get current user's account
  getCurrent: () => apiRequest<any>('/api/accounts'),

  getById: (id: string) => apiRequest<any>(`/api/accounts/${id}`),

  create: (data: { name: string }) =>
    apiRequest<any>('/api/accounts', {
      method: 'POST',
      data,
    }),

  update: (id: string, data: { name?: string; blockRoleBasedEmails?: boolean }) =>
    apiRequest<any>(`/api/accounts/${id}`, {
      method: 'PUT',
      data,
    }),

  delete: (id: string) =>
    apiRequest<void>(`/api/accounts/${id}`, {
      method: 'DELETE',
    }),

  getStats: (id: string) => apiRequest<any>(`/api/accounts/${id}/stats`),
};

// Email Config API
export const emailConfigApi = {
  getByAccount: (accountId: string, page = 1, limit = 10) =>
    apiRequest<any>(`/api/email-configs?accountId=${accountId}&page=${page}&limit=${limit}`),

  getById: (id: string) => apiRequest<any>(`/api/email-configs/${id}`),

  create: (data: any) =>
    apiRequest<any>('/api/email-configs', {
      method: 'POST',
      data,
    }),

  update: (id: string, data: any) =>
    apiRequest<any>(`/api/email-configs/${id}`, {
      method: 'PUT',
      data,
    }),

  delete: (id: string) =>
    apiRequest<void>(`/api/email-configs/${id}`, {
      method: 'DELETE',
    }),

  test: (id: string) =>
    apiRequest<{ success: boolean; message: string }>(`/api/email-configs/${id}/test`, {
      method: 'POST',
    }),

  sendTestEmail: (id: string, data: { to: string; subject?: string }) =>
    apiRequest<{ success: boolean; message: string; messageId?: string }>(`/api/email-configs/${id}/test-email`, {
      method: 'POST',
      data,
    }),

  validateConfig: (id: string) =>
    apiRequest<{ success: boolean; message: string }>(`/api/email-configs/${id}/test`, {
      method: 'POST',
    }),
};

export const accountTemplateApi = {
  getDesigns: () => apiRequest<any>('/api/account-templates/designs'),

  getAccountTemplate: (accountId: string) => apiRequest<any>(`/api/account-templates/${accountId}`),

  generatePreview: (data: any) =>
    apiRequest<Blob>('/api/account-templates/preview', {
      method: 'POST',
      data,
      responseType: 'blob',
    }),

  saveAccountTemplate: (data: any) => {
    return apiRequest<any>('/api/account-templates', {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      method: 'POST',
      data,
    });
  },
};

// Template API
export const templateApi = {
  getByAccount: (accountId: string, page = 1, limit = 10) =>
    apiRequest<any>(`/api/templates?accountId=${accountId}&page=${page}&limit=${limit}`),

  getById: (id: string) => apiRequest<any>(`/api/templates/${id}`),

  create: (data: any) =>
    apiRequest<any>('/api/templates', {
      method: 'POST',
      data,
    }),

  update: (id: string, data: any) =>
    apiRequest<any>(`/api/templates/${id}`, {
      method: 'PUT',
      data,
    }),

  delete: (id: string) =>
    apiRequest<void>(`/api/templates/${id}`, {
      method: 'DELETE',
    }),

  validateConfig: (id: string) =>
    apiRequest<any>(`/api/email-configs/${id}/validate`, {
      method: 'POST',
    }),
};

// Contact API
export const contactApi = {
  getByAccount: (accountId: string, page = 1, limit = 10, search?: string) => {
    const params = new URLSearchParams({
      accountId,
      page: page.toString(),
      limit: limit.toString(),
    });
    if (search) params.append('q', search);

    return apiRequest<any>(`/api/contacts?${params}`);
  },

  getById: (id: string) => apiRequest<any>(`/api/contacts/${id}`),

  create: (data: any) =>
    apiRequest<any>('/api/contacts', {
      method: 'POST',
      data,
    }),

  validate: (data: { accountId: string; contacts: any[] }) =>
    apiRequest<any>('/api/contacts/validate', {
      method: 'POST',
      data,
    }),

  bulkCreate: (data: { accountId: string; contacts: any[]; updateExisting?: boolean }) =>
    apiRequest<any>('/api/contacts/bulk', {
      method: 'POST',
      data,
    }),

  update: (id: string, data: any) =>
    apiRequest<any>(`/api/contacts/${id}`, {
      method: 'PUT',
      data,
    }),

  delete: (id: string) =>
    apiRequest<void>(`/api/contacts/${id}`, {
      method: 'DELETE',
    }),

  importCSV: (accountId: string, file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('accountId', accountId);

    return api.post('/api/contacts/import-csv', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).then(response => response.data);
  },
};

// Campaign API
export const campaignApi = {
  getByAccount: (accountId: string, page = 1, limit = 10) =>
    apiRequest<any>(`/api/campaigns?accountId=${accountId}&page=${page}&limit=${limit}`),

  getById: (id: string) => apiRequest<any>(`/api/campaigns/${id}`),

  create: (data: any) =>
    apiRequest<any>('/api/campaigns', {
      method: 'POST',
      data,
    }),

  update: (id: string, data: any) =>
    apiRequest<any>(`/api/campaigns/${id}`, {
      method: 'PUT',
      data,
    }),

  delete: (id: string) =>
    apiRequest<void>(`/api/campaigns/${id}`, {
      method: 'DELETE',
    }),

  // Send campaign emails
  sendEmails: (emailData: any) =>
    apiRequest<any>('/api/campaigns/send', {
      method: 'POST',
      data: emailData,
    }),

  // Get campaign recipients
  getRecipients: (campaignId: string, filter = 'all') =>
    apiRequest<any>(`/api/campaigns/${campaignId}/recipients?filter=${filter}`),

  // Get campaign follow-ups
  getFollowUps: (campaignId: string) =>
    apiRequest<any>(`/api/campaigns/${campaignId}/followups`),

  // Create follow-up campaign
  createFollowUp: (campaignId: string, data: any) =>
    apiRequest<any>(`/api/campaigns/${campaignId}/followup`, {
      method: 'POST',
      data,
    }),

  send: (campaignId: string) =>
    apiRequest<any>(`/api/campaigns/${campaignId}/send`, {
      method: 'POST',
    }),

  // Legacy endpoint for backward compatibility
  sendLegacy: (data: any) =>
    apiRequest<any>('/SendEmail', {
      method: 'POST',
      data,
    }),


};

// Analytics API (renamed to reports to avoid ad blocker issues)
export const analyticsApi = {
  getCampaignAnalytics: (accountId: string, campaignId?: string, startDate?: string, endDate?: string) => {
    const params = new URLSearchParams({ accountId });
    if (campaignId) params.append('campaignId', campaignId);
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);

    return apiRequest<any>(`/api/reports/campaigns?${params}`);
  },

  getCampaignDetails: (campaignId: string, includeFollowUps = false) => {
    const params = includeFollowUps ? '?includeFollowUps=true' : '';
    return apiRequest<any>(`/api/reports/campaigns/${campaignId}${params}`);
  },

  getEvents: (accountId: string, campaignId?: string, eventType?: string) => {
    const params = new URLSearchParams({ accountId });
    if (campaignId) params.append('campaignId', campaignId);
    if (eventType) params.append('eventType', eventType);

    return apiRequest<any>(`/api/reports/events?${params}`);
  },

  getOverview: (accountId: string) =>
    apiRequest<any>(`/api/reports/overview?accountId=${accountId}`),
};

// Utility function to get default account ID from authenticated user
export const getDefaultAccountId = (): string => {
  // Try to get from localStorage first (for backward compatibility)
  const stored = localStorage.getItem('defaultAccountId');
  if (stored) return stored;

  // Try to get from auth token
  const token = localStorage.getItem('token');
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.accountId || 'demo-account';
    } catch (error) {
      console.error('Error parsing token:', error);
    }
  }

  return 'demo-account';
};

// Utility function to set default account ID
export const setDefaultAccountId = (accountId: string): void => {
  localStorage.setItem('defaultAccountId', accountId);
};



// Function to set auth token
export const setAuthToken = (token: string | null) => {
  if (token) {
    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  } else {
    delete api.defaults.headers.common['Authorization'];
    delete axios.defaults.headers.common['Authorization'];
  }
};

// Email validation API
export const emailValidationApi = {
  validateSingle: (email: string, accountId?: string) =>
    apiRequest<{ success: boolean; result: any }>('/api/email-validation/validate', {
      method: 'POST',
      data: { email, accountId },
    }),

  validateBatch: (emails: string[], accountId?: string) =>
    apiRequest<{ success: boolean; results: any[]; summary: any }>('/api/email-validation/validate-batch', {
      method: 'POST',
      data: { emails, accountId },
    }),

  quickValidate: (emails: string[], accountId?: string) =>
    apiRequest<{ success: boolean; results: any[]; summary: any }>('/api/email-validation/quick-validate', {
      method: 'POST',
      data: { emails, accountId },
    }),

  getStats: () =>
    apiRequest<{ success: boolean; stats: any }>('/api/email-validation/stats', {
      method: 'GET',
    }),
};

export { ApiError };
